import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface QueryResult<T> {
	data: T[];
	loading: boolean;
	error: string | null;
	refetch: () => void;
	count: number | null;
}

/**
 * Generic Supabase query hook.
 * `buildQuery` receives the supabase client and should return a Postgrest query.
 * Re-fetches whenever `deps` change.
 */
export function useSupabaseQuery<T>(
	buildQuery: (
		client: typeof supabase
	) => PromiseLike<{ data: any; error: any; count?: number | null }>,
	deps: unknown[] = []
): QueryResult<T> {
	const [data, setData] = useState<T[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [count, setCount] = useState<number | null>(null);
	const [tick, setTick] = useState(0);

	const refetch = useCallback(() => setTick((t) => t + 1), []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		const query = buildQuery(supabase);
		query.then(({ data: rows, error: err, count: c }: any) => {
			if (cancelled) return;
			if (err) {
				setError(err.message);
				setData([]);
			} else {
				setData(rows ?? []);
				if (c != null) setCount(c);
			}
			setLoading(false);
		});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tick, ...deps]);

	return { data, loading, error, refetch, count };
}

/** Fetch a single row by primary key. */
export function useSupabaseRow<T>(
	table: string,
	id: string | undefined,
	select: string = '*'
): { data: T | null; loading: boolean; error: string | null; refetch: () => void } {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	const refetch = useCallback(() => setTick((t) => t + 1), []);

	useEffect(() => {
		if (!id) {
			setData(null);
			setLoading(false);
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);

		supabase
			.from(table)
			.select(select)
			.eq('id', id)
			.single()
			.then(({ data: row, error: err }: any) => {
				if (cancelled) return;
				if (err) {
					setError(err.message);
					setData(null);
				} else {
					setData(row);
				}
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table, id, select, tick]);

	return { data, loading, error, refetch };
}
